import type { ReactNode } from 'react'
import { ArrowIcon, Badge, Card, Heading, Loader } from '@autoplan/web-ui'
import { useLocale } from '@autoplan/web-sanity'
import type { TranslationKey } from '@autoplan/common'

type Priority = 'high' | 'medium' | 'low'

type TaskCardProps = {
  icon: ReactNode
  title: string
  subtitle?: ReactNode
  priority?: Priority
  onClick?: () => void
  className?: string
  isLoading?: boolean
  isError?: boolean
}

const priorityConfig: Record<
  Priority,
  { label: TranslationKey; variant: 'danger' | 'warning' | 'success' | 'primary' }
> = {
  high: { label: 'home.priority.high', variant: 'danger' },
  medium: { label: 'home.priority.medium', variant: 'warning' },
  low: { label: 'home.priority.low', variant: 'primary' },
}

export const TaskCard = ({
  icon,
  title,
  subtitle,
  priority,
  onClick,
  className,
  isLoading,
  isError,
}: TaskCardProps) => {
  const { t } = useLocale()
  const priorityItem = priority && !isLoading && !isError ? priorityConfig[priority] : null

  return (
    <div className="ap:flex ap:min-w-[280px] ap:flex-col ap:snap-start ap:xl:min-w-0 ap:xl:flex-1">
      <Card
        onClick={onClick}
        className={`ap:flex ap:h-full ap:flex-1 ap:min-w-0 ap:flex-col ap:gap-sm ap:p-2xl ${className ?? ''}`}
      >
        <div className="ap:flex ap:items-start ap:justify-between">
          <span className="ap:text-content-secondary">{icon}</span>
          <span className="ap:text-accent">
            <ArrowIcon />
          </span>
        </div>
        <Heading size="md">{title}</Heading>
        <TaskCardBody subtitle={subtitle} isLoading={isLoading} isError={isError} />
        {priorityItem && (
          <div className="ap:mt-auto ap:pt-sm">
            <Badge variant={priorityItem.variant}>{t(priorityItem.label)}</Badge>
          </div>
        )}
      </Card>
    </div>
  )
}

function TaskCardBody({ subtitle, isLoading, isError }: Pick<TaskCardProps, 'subtitle' | 'isLoading' | 'isError'>) {
  const { t } = useLocale()
  if (isLoading) return <Loader />
  if (isError) return <p className="ap:text-sm ap:text-content-danger">{t('home.fleet.loadError')}</p>
  if (subtitle) return <p className="ap:text-sm ap:text-content-secondary">{subtitle}</p>
  return null
}
